// part of nuxt
import type { $Fetch, NitroFetchRequest } from "nitropack"
import UrlConfig from "@/utils/UrlConfig"
import { getErrorStatus } from "@/utils/httpStatus"

export function settingRepository<T>(fetch: $Fetch<T, NitroFetchRequest>) {
  return {
    /**
     * Resolves every shared JobShop setting whose key matches a pattern, in one call.
     *
     * A 404 is the normal answer, not an error: setting-service seeds nothing, so a
     * JobShop that has never had a setting written from the tooling UI has no rows at
     * all. It is normalised to an empty list here rather than upstream because
     * useDataCacheWithStale deliberately rethrows 4xx — letting the 404 reach it would
     * make every unconfigured JobShop throw on every request and never cache.
     *
     * @param tenantId targeted tenant — mandatory, setting-service routes to a per-tenant database
     * @param jobShopId uuid of the JobShop whose settings are requested
     * @param keyPattern key or key pattern, where `*` is a wildcard (e.g. `jobshop.tracking.*`)
     * @returns the matching settings, or an empty list if none are written
     */
    async resolveJobShopSettings(tenantId: string, jobShopId: string, keyPattern: string): Promise<JobShopSettingValue[]> {
      try {
        return await fetch<JobShopSettingValue[]>(`${UrlConfig.PATH_BASE_SETTING_SERVICE}/public/${jobShopId}/resolve`, {
          headers: {
            "Accept": "application/json",
            "X-Tenant-Id": tenantId,
          },
          params: {
            key: keyPattern,
          },
        })
      }
      catch (error) {
        if (getErrorStatus(error) === 404)
          return []

        throw error
      }
    },
  }
}
